import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/Breadcrumbs";
import { GuiasRelacionadas } from "@/components/GuiasRelacionadas";
import hero1280Asset from "../assets/hero-1280.webp.asset.json";
import hero1920Asset from "../assets/hero-1920.webp.asset.json";
import hero640Asset from "../assets/hero-640.webp.asset.json";
import salonAsset from "../assets/casa/salon.webp.asset.json";

const srcSet = `${hero640Asset.url} 640w, ${hero1280Asset.url} 1280w, ${hero1920Asset.url} 1920w`;
const SITE = "https://elpajardebenito.es";
const URL_CANON = `${SITE}/casa-rural-escapada-romantica`;
const OG_IMAGE = `${SITE}${hero1280Asset.url}`;

const faqs = [
  { q: "¿Se puede reservar solo una habitación, sin alquilar la casa entera?", a: "Sí. Además del alquiler completo, ofrecemos habitaciones dobles con baño privado, pensadas justo para escapadas de dos personas." },
  { q: "¿Se puede reservar una sola noche?", a: "Sí, fuera de temporada alta. En puentes y festivos aplicamos una estancia mínima de 2 noches." },
  { q: "¿Hay chimenea?", a: "Sí, el salón tiene chimenea de leña, además de calefacción central en toda la casa. Es el plan de invierno por excelencia." },
  { q: "¿Se ven bien las estrellas?", a: "Muy bien. La Sierra Norte de Guadalajara es una de las zonas con menos contaminación lumínica del centro peninsular: en noches sin luna se ve la Vía Láctea a simple vista desde el patio." },
  { q: "¿Qué se puede hacer en pareja por la zona?", a: "Rutas a pie por la Sierra del Alto Rey, la cascada de Despeñalagua, los pueblos de pizarra, el Hayedo de Tejera Negra en otoño y sobremesas largas sin prisa." },
];

export const Route = createFileRoute("/casa-rural-escapada-romantica")({
  head: () => ({
    meta: [
      { title: "Escapada romántica en casa rural con chimenea · Guadalajara" },
      { name: "description", content: "Escapada romántica en la Sierra Norte de Guadalajara: habitación doble con baño privado, chimenea de leña y cielos estrellados a 1 h 30 de Madrid." },
      { property: "og:title", content: "Escapada romántica en casa rural · Guadalajara" },
      { property: "og:description", content: "Habitación doble con baño, chimenea de leña y cielo estrellado en la Sierra del Alto Rey." },
      { property: "og:url", content: URL_CANON },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: URL_CANON }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbJsonLd([
            { label: "Inicio", to: "/" },
            { label: "Escapada romántica", to: "/casa-rural-escapada-romantica" },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "Escapada romántica" }]} />
      <section className="relative h-[55vh] min-h-[380px] w-full overflow-hidden">
        <img src={hero1280Asset.url} srcSet={srcSet} sizes="100vw" alt="Atardecer en la Sierra del Alto Rey, Guadalajara" width={1920} height={1280} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 mx-auto flex h-full max-w-5xl flex-col justify-end px-4 pb-16 md:px-6">
          <p className="text-xs uppercase tracking-[0.28em] text-white/80">Escapada en pareja</p>
          <h1 className="mt-3 font-serif text-4xl text-white md:text-5xl">Escapada romántica en casa rural con chimenea</h1>
          <p className="mt-4 max-w-2xl text-white/90">Habitación doble con baño privado, leña encendida y un cielo lleno de estrellas. A 1 h 30 de Madrid.</p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 md:px-6">
        <h2 className="font-serif text-3xl text-foreground">Dos días sin cobertura mental</h2>
        <p className="mt-4 text-muted-foreground">
          Una <strong>escapada romántica</strong> no necesita mucho: una habitación bonita, silencio de verdad y un sitio donde sentarse a no hacer nada. <strong>El Pajar de Benito</strong> es una casa de piedra rehabilitada en El Ordial, un pueblo de la Sierra del Alto Rey donde después de las nueve de la noche no se oye un coche.
        </p>
        <p className="mt-4 text-muted-foreground">
          Puedes reservar solo una habitación doble con baño privado —no hace falta alquilar la casa entera— y usar el salón con chimenea, el comedor y el patio. En invierno, la leña; en verano, el fresco de la sierra y el cielo estrellado desde el jardín.
        </p>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-16 md:px-6">
        <div className="grid gap-10 md:grid-cols-2 md:items-center md:gap-16">
          <img src={salonAsset.url} alt="Salón con chimenea de leña de la casa rural" width={1600} height={1067} loading="lazy" className="aspect-[4/3] w-full rounded-sm object-cover" />
          <div>
            <h2 className="font-serif text-3xl text-foreground">Lo que hace que funcione</h2>
            <ul className="mt-6 space-y-3 text-muted-foreground">
              <li>· Habitación doble con <strong>baño privado</strong> y ropa de cama incluida.</li>
              <li>· <strong>Chimenea de leña</strong> en el salón y calefacción central.</li>
              <li>· Patio y jardín para desayunar o mirar estrellas.</li>
              <li>· Cocina equipada si preferís cenar en casa.</li>
              <li>· WiFi y parking, aunque lo suyo es no usar ninguno de los dos.</li>
              <li>· <strong>Reserva directa</strong>, sin comisiones y con trato personal.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-muted/40">
        <div className="mx-auto max-w-4xl px-4 py-16 md:px-6">
          <h2 className="font-serif text-3xl text-foreground">Planes de dos</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {[
              { t: "Amanecer en el Alto Rey", d: "Subida a la ermita de la cumbre (1.852 m) con vistas de 360°." },
              { t: "Cascada de Despeñalagua", d: "Paseo de 45 min desde Valverde de los Arroyos hasta la chorrera." },
              { t: "Noche de estrellas", d: "Cielo de baja contaminación lumínica: Vía Láctea a simple vista de mayo a septiembre." },
            ].map((b) => (
              <div key={b.t} className="rounded-sm border border-border bg-card p-6">
                <h3 className="font-serif text-lg">{b.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 md:px-6">
        <h2 className="font-serif text-3xl text-foreground">Preguntas frecuentes</h2>
        <div className="mt-8 space-y-6">
          {faqs.map((f) => (
            <div key={f.q}>
              <h3 className="font-medium text-foreground">{f.q}</h3>
              <p className="mt-2 text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <GuiasRelacionadas slugs={["estrellas-alto-rey", "subida-alto-rey", "arquitectura-negra"]} />

      <section className="mx-auto max-w-3xl px-4 py-24 text-center md:px-6">
        <h2 className="font-serif text-3xl text-foreground md:text-4xl">Elegid fechas y ya está</h2>
        <p className="mt-4 text-muted-foreground">Disponibilidad y precio real al instante, sin intermediarios.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/reserva" className="rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">Ver disponibilidad</Link>
          <Link to="/alojamiento" className="rounded-full border border-border px-6 py-3 text-sm font-medium hover:bg-muted">Ver la casa</Link>
        </div>
      </section>
    </>
  );
}
